import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import './index.scss'
import { useTranslation } from 'react-i18next'
import { INPUT_DATA, LANDING_PAGE_DATA } from './data'
import Input from '../../components/Input'
import FlotiMap from '../../components/FlotiMap'
import Button from '../../components/Button'
import { useModal } from '../../hooks/useModal'
import { useMap } from '../../hooks/useMap'
import { PROJECTION_TYPE } from '../../components/FlotiMap/context/reducer'
import { Point } from 'ol/geom'
import { useProfileSettings } from './useProfileSettings'

const ProfileSettings = () => {
  const { t } = useTranslation()

  const mapRef = useRef()
  const viewMap = useRef()

  const { formatUser, saveChanges, buttonLabel } = useProfileSettings()
  const { openModal } = useModal()
  const { degreesToRadians, radiansToDegrees } = useMap()

  const [change, setChange] = useState<any>({})
  const [error, setError] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onChanges = useCallback((value: any, key: string) => {
    setChange({ ...change, [key]: value })
  }, [change])

  const getMapPosition = useCallback(() => {
    const settings = mapRef?.current.getMapSettings()
    const projection = mapRef.current.getProjection()
    const lat_rad = settings.center[1]
    const lon_rad = settings.center[0]
    const center = new Point([lon_rad, lat_rad]).transform(projection, PROJECTION_TYPE).getFlatCoordinates()
    setChange({
      ...change,
      zoom: settings.zoom ?? change.zoom,
      center: [degreesToRadians(center[0]), degreesToRadians(center[1])]
    })
  }, [change, mapRef.current, degreesToRadians])

  const transformCenter = useCallback((centerArr, ref) => {
    const projection = ref?.current.getProjection()
    const lat = radiansToDegrees(centerArr[1])
    const lon = radiansToDegrees(centerArr[0])
    const center = new Point([lon, lat]).transform(PROJECTION_TYPE, projection)
    return center.getFlatCoordinates()
  }, [radiansToDegrees])

  const openMapModal = useCallback(() => {
    let mapSettings;
    if (change.center) {
      const center = transformCenter(change.center, viewMap)
      mapSettings = { center }
    }
    if (change.zoom) {
      mapSettings.zoom = change.zoom
    }

    openModal({
      header: t('Starting position and zoom level on maps'),
      content: (
        <div className="MapSettings__modal">
          <div className="MapSettings__modal-descr">
            {t('Zoom or move the map to the position that you would like to start with on all Floti pages.')}
          </div>
          <FlotiMap
            ref={mapRef}
            mapSettings={mapSettings}
            mapId="viewMap"
            options={{
              showTiles: false,
              showOptions: false
            }} />
        </div>
      ),
      acceptButtonLabel: t('Save current view'),
      onAccept: () => getMapPosition()
    })
  }, [openModal, t, mapRef, getMapPosition, change, transformCenter])

  useEffect(() => {
    if (change.center) {
      const center = transformCenter(change.center, viewMap)
      viewMap.current.setMapSettings({
        zoom: change.zoom,
        center
      })
    }
  }, [change.center])

  const mapSection = useMemo(() => {
    const valueData = { ...formatUser, ...change }
    return (
      <>
        <h3>{t('Map settings')}</h3>
        <span>{t('Starting position and zoom level on maps')}</span>
        <FlotiMap ref={viewMap} readonly={true} mapId="AreaMon" />
        <div className="ProfileSettings__map-settings">
          <Input
            type="number"
            size="m"
            label={t('Map latitude on load')}
            value={valueData?.center[1]}
            readonly={true}
          />
          <Input
            type="number"
            size="m"
            label={t('Map longitude on load')}
            value={valueData?.center[0]}
            readonly={true}
          />
        </div>
        <Button
          label={t('Change start position')}
          onClick={() => openMapModal()}
          size="s"
        />
      </>
    )
  }, [onChanges, t, formatUser, openMapModal])

  return (
    <div className="ProfileSettings">
      <h1>{t('Your profile information')}</h1>
      <div className="ProfileSettings__content">
        {INPUT_DATA.map((section) => {
          const { label, inputs } = section
          return (
            <div className="ProfileSettings__content-section">
              <h3>{label}</h3>

              {inputs.map((i) => {
                const valueData = { ...formatUser, ...change }
                const value = valueData[i.path] ?? ''

                if (i.path === 'map_section') {
                  return mapSection
                }

                const params = {
                  ...i,
                  value,
                  onInput: (val) => onChanges(val, i.path),
                  size: 'm'
                }

                if (i.type === 'password') params.autocomplete = false

                if (i.type === 'select' && i.path === 'landing_page') {
                  params.options = LANDING_PAGE_DATA
                }

                if (error[i.path]) params.error = error[i.path]

                return (
                  <Input {...params} />
                )
              })}
            </div>
          )
        })}
      </div>
      <Button
        label={buttonLabel(isLoading)}
        type={isLoading ? 'ghost' : 'secondary'}
        onClick={() => saveChanges(change, error, setError, setIsLoading)}
      />
    </div>
  )
}

export default ProfileSettings
